import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { achievementsApi } from '../../services/achievements.service';
import { AchievementsNotificationModal } from './AchievementsNotificationModal';
import { RoadmapModal } from './RoadmapModal';
import { RoadmapHelperTooltip } from './RoadmapHelperTooltip';
import { useIsTourActive } from '../../contexts/TourContext';

const HELPER_DISMISSED_KEY = 'mentoria.roadmap-helper-dismissed';
const LAST_ROADMAP_SHOWN_KEY = 'mentoria.roadmap-last-shown';

/**
 * Bell + Roadmap buttons with badges. Handles auto-popup on route change & login.
 *
 * Auto-popup behavior:
 * - On every route change, refetches unseen achievements and the roadmap.
 * - If there are unseen achievements → show notifications modal first.
 * - After notifications close (or if none), show roadmap modal once per day.
 * - The red badge on the bell is suppressed while a route transition is in flight
 *   to avoid "invading" the user mid-page.
 */
export function AchievementsBar() {
  const isTourActive = useIsTourActive();
  const queryClient = useQueryClient();
  const location = useLocation();
  const previousPath = useRef<string | null>(null);
  const helperDismissed = useRef<boolean>(localStorage.getItem(HELPER_DISMISSED_KEY) === '1');

  const [notifOpen, setNotifOpen] = useState(false);
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const [pendingRoadmap, setPendingRoadmap] = useState(false);
  const [navigationActive, setNavigationActive] = useState(true);

  const unseenQuery = useQuery({
    queryKey: ['achievements', 'unseen'],
    queryFn: () => achievementsApi.getUnseen(),
    refetchOnWindowFocus: false,
  });

  const roadmapQuery = useQuery({
    queryKey: ['achievements', 'roadmap'],
    queryFn: () => achievementsApi.getRoadmap(),
    refetchOnWindowFocus: false,
  });

  const ackMutation = useMutation({
    mutationFn: (ids: string[]) => achievementsApi.acknowledge(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['achievements'] });
    },
  });

  // Detect route changes & login → trigger autopopup flow.
  useEffect(() => {
    const changed = previousPath.current !== null && previousPath.current !== location.pathname;
    const justMounted = previousPath.current === null;
    previousPath.current = location.pathname;

    if (!justMounted && !changed) return;

    setNavigationActive(true);
    void (async () => {
      const [unseen, roadmap] = await Promise.all([
        queryClient.fetchQuery({ queryKey: ['achievements', 'unseen'], queryFn: () => achievementsApi.getUnseen() }),
        queryClient.fetchQuery({ queryKey: ['achievements', 'roadmap'], queryFn: () => achievementsApi.getRoadmap() }),
      ]);

      const shouldShowRoadmap = shouldShowRoadmapToday(roadmap?.remainingSteps ?? 0);

      if (unseen && unseen.length > 0) {
        setNotifOpen(true);
        setPendingRoadmap(shouldShowRoadmap);
      } else if (shouldShowRoadmap) {
        setRoadmapOpen(true);
        markRoadmapShownToday();
      }
      // Once initial autopopup is decided, allow the bell badge to appear again
      setNavigationActive(false);
    })().catch(() => setNavigationActive(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function handleNotifClose() {
    setNotifOpen(false);
    if (pendingRoadmap) {
      setPendingRoadmap(false);
      setRoadmapOpen(true);
      markRoadmapShownToday();
    }
  }

  function handleRoadmapOpenManual() {
    setRoadmapOpen(true);
    if (!helperDismissed.current) {
      setShowHelper(false); // close helper if it was open
      helperDismissed.current = true;
      localStorage.setItem(HELPER_DISMISSED_KEY, '1');
    }
  }

  function handleHelperDismiss(dontShowAgain: boolean) {
    setShowHelper(false);
    if (dontShowAgain) {
      helperDismissed.current = true;
      localStorage.setItem(HELPER_DISMISSED_KEY, '1');
    }
  }

  // Show helper tooltip once, on the second visit (so it doesn't compete with
  // the autopopups on first login). It opens 1 second after mount of a route
  // where no autopopup is showing.
  useEffect(() => {
    if (helperDismissed.current) return;
    if (notifOpen || roadmapOpen) return;
    const timer = setTimeout(() => setShowHelper(true), 1500);
    return () => clearTimeout(timer);
  }, [notifOpen, roadmapOpen, location.pathname]);

  const unseenCount = unseenQuery.data?.length ?? 0;
  // Per spec: bell badge appears only when NOT mid-navigation popup flow
  const showBellBadge = unseenCount > 0 && !navigationActive && !notifOpen;
  const roadmap = roadmapQuery.data ?? null;
  const remaining = roadmap?.remainingSteps ?? 0;
  const showRoadmapBadge = remaining > 0 && !navigationActive && !roadmapOpen;

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setNotifOpen(true)}
          title="Notificaciones de logros"
          aria-label="Notificaciones"
          className="relative w-10 h-10 rounded-full bg-surface-container-low hover:bg-surface-container text-on-surface flex items-center justify-center border border-outline-variant/15 transition-colors"
        >
          <span className="material-symbols-outlined text-[22px]">notifications</span>
          {showBellBadge && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center notification-pulse">
              {unseenCount > 9 ? '9+' : unseenCount}
            </span>
          )}
        </button>

        <button
          id="roadmap-button"
          type="button"
          onClick={handleRoadmapOpenManual}
          title="Roadmap"
          aria-label="Roadmap"
          className="relative w-10 h-10 rounded-full bg-surface-container-low hover:bg-surface-container text-on-surface flex items-center justify-center border border-outline-variant/15 transition-colors"
        >
          <span className="material-symbols-outlined text-[22px]">route</span>
          {showRoadmapBadge && (
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-primary border border-surface-container-low" />
          )}
        </button>
      </div>

      <AchievementsNotificationModal
        open={notifOpen}
        items={unseenQuery.data ?? []}
        onClose={handleNotifClose}
        onComplete={(ids) => ackMutation.mutate(ids)}
      />

      <RoadmapModal
        open={roadmapOpen}
        roadmap={roadmap}
        onClose={() => setRoadmapOpen(false)}
      />

      {showHelper && !isTourActive && (
        <RoadmapHelperTooltip
          anchorId="roadmap-button"
          onDismiss={handleHelperDismiss}
        />
      )}

      <style>{`
        @keyframes notif-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.18); }
        }
        .notification-pulse { animation: notif-pulse 1.6s ease-in-out infinite; }
      `}</style>
    </>
  );
}

function shouldShowRoadmapToday(remaining: number): boolean {
  if (remaining === 0) return false;
  const today = new Date().toISOString().slice(0, 10);
  const last = localStorage.getItem(LAST_ROADMAP_SHOWN_KEY);
  return last !== today;
}

function markRoadmapShownToday() {
  const today = new Date().toISOString().slice(0, 10);
  localStorage.setItem(LAST_ROADMAP_SHOWN_KEY, today);
}
